import { useAtomValue } from 'jotai';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'next-i18next';
import { Button } from '@forma/forma-ui-kit';
import classNames from 'classnames';
import Link from '@/components/Link';
import Logo from '@/components/Logo';

import { footerDataAtom, mainCompanyDataAtom } from '@/store/common';

import styles from './footer.module.css';
import { IMenuItem } from '@/helpers/interfaces/common.interface';

interface FooterColumnProps {
  id: string,
  name: string,
  items: (IMenuItem & { title: string })[]
}

const FooterColumn = ({ id, name, items }: FooterColumnProps) => {
  return (
    <div className={styles.column} id={id}>
      <div className={styles.title}>
        {name}
      </div>
      <div className={styles.items}>
        {items.map(({ name, href, icon, title }, index) =>
          <div className={styles.item} key={index}>
            { href ? (
              href.startsWith('http') ?
              <a href={href} className={styles.link} title={title}>
                {icon && <ReactSVG className={styles.linkIcon} src={icon} wrapper="span" />} {name}
              </a>
                :
              <Link href={href} className={styles.link} title={title}>
                {icon && <ReactSVG className={styles.linkIcon} src={icon} wrapper="span" />} {name}
              </Link>
            ) : (
              <span className={classNames(styles.link, styles.cursorDefault)}>
                {icon && <ReactSVG className={styles.linkIcon} src={icon} wrapper="span" />} {name}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const { email, phone, whatsapp, telegram, max, appStore, googlePlay } = useAtomValue(mainCompanyDataAtom);
  const footer = useAtomValue(footerDataAtom);

  return (
    <footer className={styles.root}>
      <div className={classNames(styles.container, 'container')}>
        <div className={styles.company}>
          <Logo className={styles.logo} />
          <div className={styles.description}>
            {t('footer_description')}
          </div>

          {phone && (
            <div>
              <Link href={phone.href} className={styles.contactsLink} title={phone.name}>
                {phone.icon && <ReactSVG className={styles.contactsLinkIcon} src={phone.icon} wrapper="span" />}
                {phone.name}
              </Link>
            </div>
          )}
          {email && (
            <div>
              <Link href={email.href} className={styles.contactsLink} title={email.name}>
                {email.icon && <ReactSVG className={styles.contactsLinkIcon} src={email.icon} wrapper="span" />}
                {email.name}
              </Link>
            </div>
          )}
        </div>
        {footer.map((item) =>
          <FooterColumn {...item} key={item.id} />
        )}
      </div>
      <div className={classNames(styles.copyright, 'container')}>
        <div className={styles.copyrightText}>
          © 2025. Команда сервиса DataDoc. Все права защищены.
        </div>
        <div className={styles.copyrightButtons}>
          {appStore && (
            <Link
              href={appStore.href}
              target="_blank"
              className={styles.appLink}
              title={appStore.name}
            >
              <img src={appStore.icon} width={119} height={34} alt={appStore.name} />
            </Link>
          )}
          {googlePlay && (
            <Link
              href={googlePlay.href}
              target="_blank"
              className={styles.appLink}
              title={googlePlay.name}
            >
              <img src={googlePlay.icon} width={119} height={34} alt={googlePlay.name} />
            </Link>
          )}
          {whatsapp && (
            <Button
              as={Link}
              viewStyle="secondary"
              href={whatsapp.href}
              target="_blank"
              className={styles.copyrightButton}
              iconClassName={styles.copyrightButtonIcon}
              // title={whatsapp.title}
              icon={whatsapp.icon && <ReactSVG src={whatsapp.icon} wrapper="span" />}
            />
          )}
          {max && (
            <Button
              as={Link}
              viewStyle="secondary"
              href={max.href}
              target="_blank"
              className={styles.copyrightButton}
              iconClassName={styles.copyrightButtonIcon}
              // title={max.title}
              icon={max.icon && <ReactSVG src={max.icon} wrapper="span" />}
            />
          )}
          {telegram && (
            <Button
              as={Link}
              viewStyle="secondary"
              href={telegram.href}
              target="_blank"
              className={styles.copyrightButton}
              iconClassName={styles.copyrightButtonIcon}
              // title={telegram.title}
              icon={telegram.icon && <ReactSVG src={telegram.icon} wrapper="span" />}
            />
          )}
          {/* <LangSwitchDropdown className={styles.langSwitch} align="top" /> */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
